import React, { useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonChart } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber, fmtPercent } from '../../shared/format';
import { BRAND_BLUE } from '../../shared/colors';
import { FOCUS_RING } from '../../shared/expandableRow';
import {
  DimInsightCard,
  ExportMenu,
  HOUR_LABELS,
  downloadCsv,
  downloadPdf,
  makeExport,
} from '../shared';

// Export spec — one column list drives CSV + PDF. The hour label comes from the
// row index (HOUR_LABELS[index]); the factory passes (row, index) to each getter.
// CSV emits raw values, PDF emits formatted strings + abbreviated headers.
const HOURLY_EXPORT_COLUMNS = [
  { header: 'Hour', get: (hour, index) => HOUR_LABELS[index] },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (hour) => hour.impressions, pdf: (hour) => hour.impressions.toLocaleString() },
  { header: 'Clicks', get: (hour) => hour.clicks, pdf: (hour) => hour.clicks.toLocaleString() },
  { header: 'Cost', get: (hour) => hour.cost, pdf: (hour) => fmtCurrency(hour.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (hour) => hour.conversions, pdf: (hour) => fmtNumber(hour.conversions, true) },
  { header: 'CPA', get: (hour) => (hour.conversions > 0 ? (hour.cost / hour.conversions).toFixed(2) : ''), pdf: (hour) => (hour.conversions > 0 ? fmtCurrency(hour.cost / hour.conversions) : '—') },
  { header: 'CTR', get: (hour) => (hour.impressions > 0 ? ((hour.clicks / hour.impressions) * 100).toFixed(2) + '%' : '0%'), pdf: (hour) => (hour.impressions > 0 ? fmtPercent(hour.clicks / hour.impressions) : '0.00%') },
];
const HOURLY_EXPORT = makeExport(HOURLY_EXPORT_COLUMNS);

const HOUR_BLOCKS = [
  { label: '12am – 3am', start: 0, end: 4 },
  { label: '4am – 7am', start: 4, end: 8 },
  { label: '8am – 11am', start: 8, end: 12 },
  { label: '12pm – 3pm', start: 12, end: 16 },
  { label: '4pm – 7pm', start: 16, end: 20 },
  { label: '8pm – 11pm', start: 20, end: 24 },
];

const HourOfDay = ({ hourlyData, loading, error, expanded, onToggle, rangeStart, rangeEnd }) => {
  const [expandedBlocks, setExpandedBlocks] = useState({});
  const [openDetails, setOpenDetails] = useState({});

  const exportCsv = () => {
    if (!hourlyData?.items?.length) return;
    downloadCsv(HOURLY_EXPORT.headers, hourlyData.items.map(HOURLY_EXPORT.toCsvRow), `hourly-performance-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!hourlyData?.items?.length) return;
    downloadPdf('Hourly Performance', HOURLY_EXPORT.pdfHeaders, hourlyData.items.map(HOURLY_EXPORT.toPdfRow), `hourly-performance-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
          </svg>
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Hour of Day Performance</span>
          <InfoTip text="Clicks and conversions by hour, aggregated across the range. Spot the hours where ads convert vs. where they just burn budget." />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{expanded ? 'Collapse' : 'Expand'}</span>
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t dark:border-gray-800">
          {error ? (
            <div className="pt-4"><ErrorState message={error} /></div>
          ) : loading ? (
            <div className="pt-4">
              <SkeletonChart height={240} />
            </div>
          ) : !hourlyData?.items?.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No hourly data available for this period.</p>
          ) : (
            <div className="pt-4">
              <div className="flex justify-end pb-2">
                <ExportMenu onCsv={exportCsv} onPdf={exportPdf} />
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={hourlyData.items.map((hour, index) => ({ ...hour, label: HOUR_LABELS[index] }))} margin={{ top: 20, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} interval={2} axisLine={{ stroke: '#374151' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(value) => `$${value}`} axisLine={false} tickLine={false} width={45} />
                  <ThemedChartTooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const datum = payload[0].payload;
                      return (
                        <ThemedTooltipBox title={label}>
                          <div>Cost: {fmtCurrency(datum.cost)}</div>
                          <div>Clicks: {datum.clicks.toLocaleString()}</div>
                          <div>Impr: {datum.impressions.toLocaleString()}</div>
                          <div>Conv: {fmtNumber(datum.conversions, true)}</div>
                          <div>CPA: {datum.cpa != null ? fmtCurrency(datum.cpa) : '—'}</div>
                          <div>CTR: {fmtPercent(datum.ctr)}</div>
                        </ThemedTooltipBox>
                      );
                    }}
                  />
                  <Bar dataKey="cost" fill={BRAND_BLUE} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              <ThinScroll className="responsive-card-table-wrapper mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                <table className="responsive-card-table w-full text-xs min-w-[640px]">
                  <thead>
                    <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <th className="text-left py-2 px-3 font-medium">Time Block</th>
                      <th className="text-right py-2 px-3 font-medium">Impr.</th>
                      <th className="text-right py-2 px-3 font-medium">Clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Cost</th>
                      <th className="text-right py-2 px-3 font-medium">Conv.</th>
                      <th className="text-right py-2 px-3 font-medium">CPA</th>
                      <th className="text-right py-2 px-3 font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {HOUR_BLOCKS.map((block) => {
                      const items = hourlyData.items.slice(block.start, block.end);
                      const totals = items.reduce((acc, hour) => ({
                        impressions: acc.impressions + hour.impressions,
                        clicks: acc.clicks + hour.clicks,
                        cost: acc.cost + hour.cost,
                        conversions: acc.conversions + hour.conversions,
                      }), { impressions: 0, clicks: 0, cost: 0, conversions: 0 });
                      const cpa = totals.conversions > 0 ? totals.cost / totals.conversions : null;
                      const ctr = totals.impressions > 0 ? totals.clicks / totals.impressions : 0;
                      const isOpen = expandedBlocks[block.label] || false;
                      return (
                        <React.Fragment key={block.label}>
                          {/* Unlike the sibling breakdown panels this row cannot take
                              role="button": it already contains a real <button> (the
                              mobile "View Details" toggle for a different piece of
                              state), and a button nested inside role="button" is
                              invalid. The keyboard affordance is therefore a button in
                              the first cell, matching the CampaignTable sort pattern. */}
                          <tr
                            onClick={() => setExpandedBlocks((prev) => ({ ...prev, [block.label]: !prev[block.label] }))}
                            className={`border-b dark:border-gray-800 cursor-pointer transition-colors ${isOpen ? '' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'} ${!openDetails[block.label] ? 'rc-collapsed' : ''}`}
                          >
                            <td className={`rc-title-cell py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium ${isOpen || openDetails[block.label] ? 'rc-title-open' : ''}`}>
                              <button
                                type="button"
                                onClick={(event) => { event.stopPropagation(); setExpandedBlocks((prev) => ({ ...prev, [block.label]: !prev[block.label] })); }}
                                aria-expanded={isOpen}
                                aria-label={`${isOpen ? 'Collapse' : 'Expand'} hourly detail for ${block.label}`}
                                className={`flex items-center gap-1.5 font-medium ${FOCUS_RING} rounded-sm`}
                              >
                                <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                {block.label}
                              </button>
                              <button
                                type="button"
                                onClick={(event) => { event.stopPropagation(); setOpenDetails((prev) => ({ ...prev, [block.label]: !prev[block.label] })); }}
                                className="hidden max-[499px]:inline text-xs font-medium text-[#32B1FF] hover:underline"
                              >
                                {openDetails[block.label] ? 'Hide Details' : 'View Details'}
                              </button>
                            </td>
                            <td data-label="Impr." className="text-right px-3 text-gray-700 dark:text-gray-300 font-medium">{totals.impressions.toLocaleString()}</td>
                            <td data-label="Clicks" className="text-right px-3 text-gray-700 dark:text-gray-300 font-medium">{totals.clicks.toLocaleString()}</td>
                            <td data-label="Cost" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtCurrency(totals.cost)}</td>
                            <td data-label="Conv." className="text-right px-3 text-gray-700 dark:text-gray-300 font-medium">{fmtNumber(totals.conversions, true)}</td>
                            <td data-label="CPA" className="text-right px-3 text-gray-700 dark:text-gray-300 font-medium">{cpa != null ? fmtCurrency(cpa) : '—'}</td>
                            <td data-label="CTR" className="text-right px-3 text-gray-700 dark:text-gray-300 font-medium">{fmtPercent(ctr)}</td>
                          </tr>
                          {isOpen && items.map((hour, index) => (
                            <tr key={hour.hour_of_day} className="border-b dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                              <td className="rc-title-cell py-2 pl-8 pr-3 text-gray-500 dark:text-gray-400 max-[499px]:!text-[#32B1FF] max-[499px]:font-semibold">{HOUR_LABELS[block.start + index]}</td>
                              <td data-label="Impr." className="text-right px-3 text-gray-500 dark:text-gray-400">{hour.impressions.toLocaleString()}</td>
                              <td data-label="Clicks" className="text-right px-3 text-gray-500 dark:text-gray-400">{hour.clicks.toLocaleString()}</td>
                              <td data-label="Cost" className="text-right px-3 text-gray-500 dark:text-gray-400">{fmtCurrency(hour.cost)}</td>
                              <td data-label="Conv." className="text-right px-3 text-gray-500 dark:text-gray-400">{fmtNumber(hour.conversions, true)}</td>
                              <td data-label="CPA" className="text-right px-3 text-gray-500 dark:text-gray-400">{hour.cpa != null ? fmtCurrency(hour.cpa) : '—'}</td>
                              <td data-label="CTR" className="text-right px-3 text-gray-500 dark:text-gray-400">{fmtPercent(hour.ctr)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </ThinScroll>

              <DimInsightCard
                insight={hourlyData.insight}
                totalClicks={hourlyData.items.reduce((sum, hour) => sum + hour.clicks, 0)}
                totalConv={hourlyData.items.reduce((sum, hour) => sum + hour.conversions, 0)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HourOfDay;
